import React from 'react';

const TermsOfService: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Terms of Service for Catalog Muse</h1>
                <p className="text-slate-500 mb-8">Effective Date: November 25, 2025</p>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Acceptance of Terms</h2>
                    <p className="text-slate-600 leading-relaxed">
                        By installing and using the <strong>Catalog Muse</strong> Shopify App ("the App"), you agree to be bound by these Terms of Service ("Terms").
                        If you do not agree to these Terms, please uninstall the App immediately.
                    </p>
                    <p className="text-slate-600 leading-relaxed mt-3">
                        The App is operated by <strong>Amin Boulabaim Amjahid</strong> ("we", "us", or "our").
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Description of Service</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">Catalog Muse is an AI-powered content generation tool for Shopify merchants. It allows users to:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li>Generate blog posts based on product data.</li>
                        <li>Create images for Pinterest pins and product showcases.</li>
                        <li>Sync Shopify product catalogs with a Pinecone vector database for semantic search and recommendations.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">3. User Responsibilities</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">You are responsible for:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                        <li><strong>Pinecone Account:</strong> You must maintain your own Pinecone account. You are responsible for any costs associated with your Pinecone usage.</li>
                        <li><strong>API Credentials:</strong> You must keep your Pinecone API keys and Shopify credentials secure.</li>
                        <li><strong>Content Accuracy:</strong> AI-generated content may contain inaccuracies. You agree to review and edit all content (blogs, images, descriptions) before publishing it to your live store. We are not responsible for false or misleading information published on your store.</li>
                        <li><strong>Compliance:</strong> You must ensure that your use of the App and the content you generate complies with all applicable laws and regulations.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">4. AI Usage and Limitations</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">The App uses third-party AI services (e.g., Google Gemini) to generate content.</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li><strong>No Guarantee:</strong> We do not guarantee the uniqueness, quality, or accuracy of the generated content.</li>
                        <li><strong>Availability:</strong> Service availability depends on the uptime of third-party providers (Shopify, Pinecone, Google AI).</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Intellectual Property</h2>
                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                        <li><strong>App IP:</strong> The App's code, design, and branding are owned by Amin Boulabaim Amjahid.</li>
                        <li><strong>Your Content:</strong> You retain ownership of the content generated for your store.</li>
                        <li><strong>Feedback:</strong> Any suggestions or feedback you provide may be used by us to improve the App without compensation.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Payment and Billing</h2>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li>The App may offer free and paid plans.</li>
                        <li>All billing is handled directly through <strong>Shopify Billing</strong>.</li>
                        <li>Refunds are subject to Shopify's refund policy and our discretion.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Termination</h2>
                    <p className="text-slate-600 leading-relaxed">
                        We reserve the right to terminate or suspend your access to the App at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Limitation of Liability</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">
                        To the fullest extent permitted by law, <strong>Amin Boulabaim Amjahid</strong> shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                    </p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li>Your access to or use of or inability to access or use the App.</li>
                        <li>Any conduct or content of any third party on the App.</li>
                        <li>Any content obtained from the App.</li>
                        <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Governing Law</h2>
                    <p className="text-slate-600 leading-relaxed">
                        These Terms shall be governed and construed in accordance with the laws of <strong>Spain</strong>, without regard to its conflict of law provisions.
                    </p>
                </section>

                <section className="border-t border-slate-200 pt-6">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">10. Contact Information</h2>
                    <p className="text-slate-600 leading-relaxed">
                        For any questions about these Terms, please contact us at:<br /><br />
                        <strong>Amin Boulabaim Amjahid</strong><br />
                        C/ Loma de los Riscos Nº59, 29620 Málaga, Spain<br />
                        📧 amin@innerunionhome.com
                    </p>
                </section>
            </div>
        </div>
    );
};

export default TermsOfService;
